import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Car, ScanSearch, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SearchSelect } from "./SearchSelect";
import { useVehicle } from "../context/VehicleContext";
import { decodeVin } from "../lib/api";

const Field = ({ label, value, onChange, options, testId, placeholder, disabled }) => (
  <div>
    <label className="font-mono text-[10px] tracking-widest uppercase text-slate-400 block mb-1.5">{label}</label>
    <SearchSelect value={value ? String(value) : ""} onChange={onChange} options={options} testId={testId} placeholder={placeholder} disabled={disabled} />
  </div>
);

export const VehiclePicker = () => {
  const { selection, makes, modelsFor, yearsFor, setMake, setModel, setYear, applyDecoded } = useVehicle();
  const { t } = useTranslation();
  const [vin, setVin] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDecode = async () => {
    const v = vin.trim().toUpperCase();
    if (v.length < 11) { toast.error("Enter a full VIN (17 characters)."); return; }
    setLoading(true);
    try {
      const data = await decodeVin(v);
      const label = `${data.year || ""} ${data.make} ${data.model}`.trim();
      const res = applyDecoded(data);
      if (res.ok) {
        toast.success("VIN decoded", { description: `Matched ${res.year} ${res.make} ${res.model}` });
      } else {
        toast.warning("Decoded but not in catalog", { description: `${label} — pick the closest match manually.` });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "VIN lookup failed. Try again or select manually.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl bg-surface border border-steel p-5" data-testid="vehicle-picker">
      <div className="flex items-center gap-2 mb-4">
        <Car size={18} className="text-cyan" />
        <h2 className="font-heading font-bold uppercase tracking-widest text-base">{t("garage.select")}</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label={t("garage.make")} testId="picker-make" value={selection.make} onChange={setMake} options={makes} placeholder={t("garage.make")} />
        <Field label={t("garage.model")} testId="picker-model" value={selection.model} onChange={setModel} options={modelsFor(selection.make)} placeholder={t("garage.model")} disabled={!selection.make} />
        <Field label={t("garage.year")} testId="picker-year" value={selection.year} onChange={setYear} options={yearsFor(selection.make, selection.model)} placeholder={t("garage.year")} disabled={!selection.model} />
      </div>

      <div className="mt-4 pt-4 border-t border-steel/60">
        <label className="font-mono text-[10px] tracking-widest uppercase text-slate-400 block mb-1.5">
          {t("garage.orVin")}
        </label>
        <div className="flex gap-2">
          <input
            data-testid="vin-input"
            value={vin}
            onChange={(e) => setVin(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleDecode()}
            maxLength={17}
            placeholder="1GYEK63N76R..."
            className="flex-1 h-10 px-3 rounded-lg bg-ink border border-steel text-white font-mono text-sm tracking-wider placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-cyan uppercase"
          />
          <button
            data-testid="vin-decode-btn"
            onClick={handleDecode}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-cyan text-ink font-mono text-xs uppercase tracking-widest font-bold hover:bg-cyan/90 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <ScanSearch size={15} />}
            {loading ? t("garage.decoding") : t("garage.decode")}
          </button>
        </div>
        <p className="font-mono text-[9px] text-slate-600 mt-1.5 tracking-wide">
          {t("garage.vinHelp")}
        </p>
      </div>
    </div>
  );
};
